import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Eye } from 'lucide-react';
import { adminApi } from '../../services/api';
import { Avatar } from '../../components/ui/Avatar';
import { Skeleton } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { formatDate, getStatusColor } from '../../utils/helpers';
import type { PaginatedResponse, User } from '../../types';

const userTypeLabels: Record<string, string> = {
  client: 'Cliente',
  worker: 'Trabajador',
  admin: 'Admin',
};

const statusOptions = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'suspended', label: 'Suspendido' },
];

const verificationOptions = [
  { value: 'pending', label: 'Pendientes', className: 'bg-warning-100 text-warning-700' },
  { value: 'approved', label: 'Aprobados', className: 'bg-success-100 text-success-700' },
  { value: 'rejected', label: 'Rechazados', className: 'bg-danger-100 text-danger-700' },
];

export const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState<PaginatedResponse<User> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [userType, setUserType] = useState('');
  const [status, setStatus] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [page, setPage] = useState(1);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .users({
        search: search || undefined,
        user_type: userType || undefined,
        status: status || undefined,
        verification_status: verificationStatus || undefined,
        page,
      })
      .then((res) => setResult(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, userType, status, verificationStatus]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const handleStatusChange = async (userId: number, newStatus: string) => {
    setUpdatingId(userId);
    try {
      await adminApi.updateUserStatus(userId, newStatus);
      setResult((prev) =>
        prev
          ? { ...prev, data: prev.data.map((u) => (u.id === userId ? { ...u, status: newStatus as User['status'] } : u)) }
          : prev
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-onSurface">Usuarios</h1>
        <p className="text-onSurface-mute">Gestioná clientes, trabajadores y administradores</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-onSurface-mute" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface placeholder-onSurface-mute focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </form>

        <select
          value={userType}
          onChange={(e) => {
            setPage(1);
            setUserType(e.target.value);
          }}
          className="px-3 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">Todos los tipos</option>
          <option value="client">Cliente</option>
          <option value="worker">Trabajador</option>
          <option value="admin">Admin</option>
        </select>

        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          className="px-3 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">Todos los estados</option>
          {statusOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          value={verificationStatus}
          onChange={(e) => {
            setPage(1);
            setVerificationStatus(e.target.value);
          }}
          className="px-3 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">Toda verificación</option>
          {verificationOptions.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-surface rounded-2xl border border-outlineVarWarm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : result && result.data.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-outlineVarWarm text-left text-onSurface-mute">
                <th className="px-5 py-3 font-medium">Usuario</th>
                <th className="px-5 py-3 font-medium">Tipo</th>
                <th className="px-5 py-3 font-medium">Estado</th>
                <th className="px-5 py-3 font-medium">Verificación</th>
                <th className="px-5 py-3 font-medium">Alta</th>
                <th className="px-5 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outlineVarWarm">
              {result.data.map((u) => (
                <tr key={u.id}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar src={u.avatar} name={u.full_name} size="sm" />
                      <div className="min-w-0">
                        <p className="font-medium text-onSurface truncate">{u.full_name}</p>
                        <p className="text-xs text-onSurface-mute truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-onSurface-var">{userTypeLabels[u.user_type] || u.user_type}</td>
                  <td className="px-5 py-3">
                    <span className={`badge ${getStatusColor(u.status)}`}>
                      {statusOptions.find((s) => s.value === u.status)?.label || u.status}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {u.user_type === 'worker' && u.verification_status ? (
                      <span
                        className={`badge ${
                          verificationOptions.find((v) => v.value === u.verification_status)?.className || ''
                        }`}
                      >
                        {verificationOptions.find((v) => v.value === u.verification_status)?.label || u.verification_status}
                      </span>
                    ) : (
                      <span className="text-onSurface-mute">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-onSurface-var">{formatDate(u.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end items-center gap-1">
                      <button
                        onClick={() => navigate(`/admin/users/${u.id}`)}
                        className="p-2 rounded-lg text-onSurface-var hover:bg-surface-var transition-colors"
                        title="Ver detalle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <select
                        value={u.status}
                        disabled={updatingId === u.id}
                        onChange={(e) => handleStatusChange(u.id, e.target.value)}
                        className="text-xs px-2 py-1.5 rounded-lg border border-outlineVarWarm bg-surface text-onSurface disabled:opacity-50"
                      >
                        {statusOptions.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-onSurface-mute text-center py-12">No se encontraron usuarios</p>
        )}
      </div>

      {result && <Pagination meta={result} onPageChange={setPage} />}
    </div>
  );
};
